
import { useState, useEffect, useRef, type FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, ArrowRight, ArrowLeft, Check, Shield, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import AuthShell, {
  AuthField,
  AuthNotice,
  PasswordToggle,
  authActionClass,
  authLinkClass,
} from '../../../components/auth/AuthShell';
import Button from '../../../components/base/Button';
import { resolveFrom } from '../../../utils/safeRedirect';

interface FormData {
  email: string;
  password: string;
}

interface FormErrors {
  email?: string;
  password?: string;
  totp?: string;
  general?: string;
}

type View = 'password' | 'forgot' | 'totp';

/** What other pages may pass to /signin in router state. */
interface SignInRouteState {
  /** An i18n key in `common` (e.g. 'signin_to_save_favorites') or plain text. */
  message?: unknown;
  /** Where to return afterwards: a path or a Location. See utils/safeRedirect. */
  from?: unknown;
  /** 'forgot' opens the reset-link form straight away (the expired-link page sends this). */
  view?: unknown;
  /** Pre-fills the email field. */
  email?: unknown;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const focusField = (id: string, select = false) => {
  const field = document.getElementById(id) as HTMLInputElement | null;
  field?.focus();
  if (select) field?.select();
};

export default function SignInPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, verifyTotp, resetPassword } = useAuth();

  const routeState: SignInRouteState =
    location.state && typeof location.state === 'object' ? location.state : {};
  const stateMessage = typeof routeState.message === 'string' ? routeState.message : '';
  const prefillEmail = typeof routeState.email === 'string' ? routeState.email : '';
  // `stateMessage` is an i18n key passed in navigation state. Translate it; a plain
  // sentence that is not a key comes back unchanged through defaultValue.
  const notice = stateMessage ? t(stateMessage, { defaultValue: stateMessage }) : '';
  // Where to go once signed in: the page that sent the visitor here (state.from as a
  // path or a Location, or ?redirect= / ?returnUrl=), never another site or /signin.
  const from = resolveFrom(location.state, location.search);

  const [formData, setFormData] = useState<FormData>({ email: prefillEmail, password: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(routeState.view === 'forgot');
  const [successMessage, setSuccessMessage] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  // TOTP stage
  const [stage, setStage] = useState<'password' | 'totp'>('password');
  const [totpToken, setTotpToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [showBackupHint, setShowBackupHint] = useState(false);

  const view: View = stage === 'totp' ? 'totp' : showForgotPassword ? 'forgot' : 'password';
  const formRef = useRef<HTMLFormElement>(null);
  const lastView = useRef<View>(view);

  useEffect(() => {
    setFormData({ email: prefillEmail, password: '' });
    setErrors({});
    setSuccessMessage('');
  }, [stateMessage, prefillEmail]);

  // When the card switches between sign-in, forgot-password and the 2FA code,
  // focus moves to the first field of the form that just appeared.
  useEffect(() => {
    if (lastView.current === view) return;
    lastView.current = view;
    formRef.current?.querySelector<HTMLInputElement>('input:not([type="checkbox"])')?.focus();
  }, [view]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = t('auth.emailRequired');
    } else if (!EMAIL_PATTERN.test(formData.email.trim())) {
      newErrors.email = t('auth.emailInvalid');
    }
    if (!formData.password) {
      newErrors.password = t('auth.passwordRequired');
    }
    setErrors(newErrors);
    // On failure, move focus to the first errored field for keyboard/SR users.
    if (newErrors.email) {
      focusField('signin-email');
    } else if (newErrors.password) {
      focusField('signin-password');
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    setErrors({});
    try {
      const result = await signIn(formData.email, formData.password);

      if (result.requiresTotp && result.totpToken) {
        // Admin with TOTP enrolled — switch to TOTP stage without storing tokens
        setTotpToken(result.totpToken);
        setTotpCode('');
        setShowBackupHint(false);
        setStage('totp');
        return;
      }

      if (result.mustEnrollTotp) {
        // Admin without TOTP enrolled — go set up 2FA
        navigate('/admin/setup-2fa', { replace: true });
        return;
      }

      // Go straight back. This navigation lands in the same render as the new
      // session, so the guest-only guard around /signin (ProtectedRoute) never
      // gets to send the visitor to its own default instead.
      navigate(from, { replace: true });
    } catch (error: unknown) {
      const errorObj = error as { message?: string; details?: { code?: string }; status?: number };
      const message = errorObj.message || '';
      const code = errorObj.details?.code || '';
      const status = errorObj.status || 0;
      let errorMessage = t('auth.unexpectedError');
      if (code === 'INVALID_CREDENTIALS' || message.includes('Invalid login credentials')) {
        errorMessage = t('auth.invalidCredentials');
      } else if (code === 'ACCOUNT_DELETED' || message.includes('deleted')) {
        errorMessage = t('auth.accountDeleted');
      } else if (code === 'ACCOUNT_SUSPENDED' || message.includes('suspended')) {
        errorMessage = t('auth.accountSuspended');
      } else if (code === 'ACCOUNT_INACTIVE' || message.includes('inactive')) {
        errorMessage = t('auth.accountInactive');
      } else if (message.includes('Email not confirmed') || message.includes('verify your email')) {
        errorMessage = t('auth.emailNotConfirmed');
      } else if (message.includes('too many requests') || status === 429) {
        errorMessage = t('auth.tooManyRequests');
      } else if (status === 0 || message.includes('Unable to connect')) {
        errorMessage = t('auth.networkError');
      }
      // Anything else keeps the generic message: the raw text is English, or a bare
      // code such as SERVER_ERROR, and never belongs on the page.
      setErrors({ general: errorMessage });
      focusField('signin-password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTotpSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (totpCode.length < 6) return;
    setIsLoading(true);
    setErrors({});
    try {
      await verifyTotp(totpToken, totpCode);
      navigate(from, { replace: true });
    } catch (error: unknown) {
      // The API answers a wrong, expired or locked-out code with one generic 401,
      // in English; show our own words instead of the raw text.
      const errorObj = error as { details?: { code?: string }; status?: number };
      const code = errorObj.details?.code || '';
      const status = errorObj.status || 0;
      if (status === 429) {
        setErrors({ general: t('auth.tooManyRequests') });
      } else if (code === 'INVALID_CREDENTIALS' || status === 401 || status === 400) {
        setErrors({ totp: t('auth.invalidTotpCode') });
        focusField('signin-totp', true);
      } else if (status === 0) {
        setErrors({ general: t('auth.networkError') });
      } else {
        setErrors({ general: t('auth.unexpectedError') });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: FormEvent) => {
    e.preventDefault();
    const email = formData.email.trim();
    if (!email || !EMAIL_PATTERN.test(email)) {
      setErrors({ email: email ? t('auth.emailInvalid') : t('auth.emailForReset') });
      focusField('forgot-email');
      return;
    }
    setIsLoading(true);
    setErrors({});
    try {
      await resetPassword(formData.email);
      setSuccessMessage(t('auth.passwordResetSent'));
      setShowForgotPassword(false);
    } catch {
      setErrors({ general: t('auth.passwordResetFailed') });
    } finally {
      setIsLoading(false);
    }
  };

  const openForgotPassword = () => {
    setErrors({});
    setSuccessMessage('');
    setShowForgotPassword(true);
  };

  const closeForgotPassword = () => {
    setErrors({});
    setShowForgotPassword(false);
  };

  const toggleBackupCode = () => {
    setShowBackupHint((v) => !v);
    setTotpCode('');
    setErrors({});
    focusField('signin-totp');
  };

  const leaveTotp = () => {
    setStage('password');
    setTotpToken('');
    setTotpCode('');
    setShowBackupHint(false);
    setErrors({});
  };

  const mutedActionClass = `${authActionClass} text-[var(--pv-color-text-muted)] hover:text-[var(--pv-color-primary)]`;
  const primaryActionClass = `${authActionClass} font-bold text-[var(--pv-color-primary)]`;
  const backIcon = <ArrowLeft className="h-[1.125rem] w-[1.125rem] shrink-0 rtl:-scale-x-100" aria-hidden="true" />;
  // Points forward: right in English, mirrored to the left in Arabic.
  const forwardIcon = <ArrowRight className="h-5 w-5 shrink-0 rtl:-scale-x-100" aria-hidden="true" />;

  return (
    <AuthShell
      title={t('auth.welcomeBack')}
      subtitle={t('auth.signInSubtitle')}
      backTo="/"
      backLabel={t('auth.backToHome')}
    >
      {notice ? <AuthNotice tone="info">{notice}</AuthNotice> : null}
      {successMessage ? <AuthNotice tone="success">{successMessage}</AuthNotice> : null}
      {errors.general ? <AuthNotice tone="error">{errors.general}</AuthNotice> : null}

      {view === 'totp' ? (
        /* TOTP Verification Step */
        <form ref={formRef} onSubmit={handleTotpSubmit} noValidate className="space-y-5">
          <div className="text-center">
            <span
              className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)]"
              aria-hidden="true"
            >
              <ShieldCheck className="h-6 w-6" />
            </span>
            <h2 className="text-lg font-bold text-[var(--pv-color-primary)]">
              {showBackupHint ? t('auth.backupCodeTitle') : t('auth.twoFactorTitle')}
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-[var(--pv-color-text-muted)]">
              {showBackupHint ? t('auth.backupCodeSubtitle') : t('auth.twoFactorSubtitle')}
            </p>
          </div>

          <AuthField
            id="signin-totp"
            name="totp"
            type="text"
            label={showBackupHint ? t('auth.backupCode') : t('auth.verificationCode')}
            icon={Shield}
            inputMode={showBackupHint ? 'text' : 'numeric'}
            pattern={showBackupHint ? undefined : '[0-9]*'}
            maxLength={showBackupHint ? 10 : 6}
            value={totpCode}
            onChange={(e) => {
              // Backup codes are 10 lowercase letters and digits; phones like to
              // capitalise the first letter, and pasted codes can carry spaces.
              setTotpCode(
                showBackupHint
                  ? e.target.value.replace(/\s+/g, '').toLowerCase()
                  : e.target.value.replace(/\D/g, '')
              );
              if (errors.totp) setErrors((prev) => ({ ...prev, totp: undefined }));
            }}
            placeholder={showBackupHint ? 'XXXXXXXXXX' : '000000'}
            // Balance the icon's padding so the code sits in the middle of the field.
            className="pe-11 text-center tracking-widest"
            readOnly={isLoading}
            required
            autoComplete="one-time-code"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            error={errors.totp}
          />

          <Button type="submit" size="lg" className="w-full" loading={isLoading} disabled={totpCode.length < 6}>
            {isLoading ? t('auth.verifying') : (
              <>
                {t('auth.verify')}
                {forwardIcon}
              </>
            )}
          </Button>

          <div className="flex flex-col items-center">
            <button type="button" onClick={toggleBackupCode} disabled={isLoading} className={primaryActionClass}>
              {showBackupHint ? t('auth.useAuthenticatorApp') : t('auth.useBackupCode')}
            </button>
            <button type="button" onClick={leaveTotp} disabled={isLoading} className={mutedActionClass}>
              {backIcon}
              {t('auth.backToSignIn')}
            </button>
          </div>
        </form>
      ) : view === 'forgot' ? (
        /* Forgot Password Form */
        <form ref={formRef} onSubmit={handleForgotPassword} noValidate className="space-y-5">
          <div className="text-center">
            <h2 className="text-lg font-bold text-[var(--pv-color-primary)]">{t('auth.resetPasswordTitle')}</h2>
            <p className="mt-1 text-sm leading-relaxed text-[var(--pv-color-text-muted)]">
              {t('auth.resetPasswordSubtitle')}
            </p>
          </div>

          <AuthField
            id="forgot-email"
            type="email"
            name="email"
            label={t('auth.email')}
            icon={Mail}
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder={`${t('auth.enterEmail')}…`}
            readOnly={isLoading}
            required
            autoComplete="email"
            spellCheck={false}
            error={errors.email}
          />

          <Button type="submit" size="lg" className="w-full" loading={isLoading}>
            {isLoading ? t('auth.sending') : t('auth.sendResetLink')}
          </Button>

          <div className="flex justify-center">
            <button type="button" onClick={closeForgotPassword} disabled={isLoading} className={mutedActionClass}>
              {backIcon}
              {t('auth.backToSignIn')}
            </button>
          </div>
        </form>
      ) : (
        /* Sign In Form */
        <>
          <form ref={formRef} onSubmit={handleSubmit} noValidate className="space-y-5">
            <AuthField
              id="signin-email"
              type="email"
              name="email"
              label={t('auth.email')}
              icon={Mail}
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder={`${t('auth.enterEmail')}…`}
              readOnly={isLoading}
              required
              autoComplete="email"
              spellCheck={false}
              error={errors.email}
            />

            <AuthField
              id="signin-password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              label={t('auth.password')}
              icon={Lock}
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              placeholder={`${t('auth.enterPassword')}…`}
              readOnly={isLoading}
              required
              autoComplete="current-password"
              error={errors.password}
              endAdornment={
                <PasswordToggle
                  visible={showPassword}
                  onToggle={() => setShowPassword((v) => !v)}
                  controls="signin-password"
                  label={t('auth.showPassword')}
                />
              }
            />

            {/* Remember me & Forgot */}
            <div className="flex flex-wrap items-center justify-between gap-x-4">
              <label
                htmlFor="remember-me"
                className="flex min-h-[2.75rem] cursor-pointer items-center gap-2.5 text-[0.9375rem] text-[var(--pv-color-text)]"
              >
                {/* Visually-hidden real input (keeps accessibility); the
                    box below is styled from React state so the checked
                    fill + white tick are always clearly visible, and it
                    takes the focus ring when the input has keyboard focus. */}
                <input
                  id="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={() => setRememberMe(!rememberMe)}
                  className="peer sr-only"
                />
                <span
                  aria-hidden="true"
                  className={`flex h-[1.125rem] w-[1.125rem] shrink-0 items-center justify-center rounded-md border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-[#004D40] peer-focus-visible:ring-offset-2 dark:peer-focus-visible:ring-[#7FD6C3] dark:peer-focus-visible:ring-offset-[var(--pv-color-surface-strong)] ${
                    rememberMe
                      ? 'border-[#004D40] bg-[#004D40] text-white dark:border-[#3FBBA2] dark:bg-[#3FBBA2] dark:text-[#002A23]'
                      : 'border-[var(--pv-color-text-subtle)] bg-[var(--pv-color-surface-strong)]'
                  }`}
                >
                  {rememberMe ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : null}
                </span>
                {t('auth.rememberMe')}
              </label>
              <button type="button" onClick={openForgotPassword} disabled={isLoading} className={primaryActionClass}>
                {t('auth.forgotPassword')}
              </button>
            </div>

            <Button type="submit" size="lg" className="w-full" loading={isLoading}>
              {isLoading ? t('auth.signingIn') : (
                <>
                  {t('auth.signIn')}
                  {forwardIcon}
                </>
              )}
            </Button>
          </form>

          {/* Sign up link */}
          <p className="mt-6 border-t border-[var(--pv-border-subtle)] pt-5 text-center text-[0.9375rem] text-[var(--pv-color-text-muted)]">
            {t('auth.noAccount')}{' '}
            <Link to="/register" className={authLinkClass}>
              {t('auth.signUpFree')}
            </Link>
          </p>
        </>
      )}
    </AuthShell>
  );
}
